import React, { Fragment, useContext, useRef, useState } from "react";
import { Dialog, Transition, Switch } from "@headlessui/react";
import Button from "../../VibehutUI/Button";
import { StoreContext } from "../../Context/StoreContext";
import { updateEvent } from "../../actions/eventActions";
import { updateEventCacheData } from "../../services/eventService";

import Loader from "../../VibehutUI/Loader";
import Back from "../../VibehutUI/Icons/Back";
import Close from "../../VibehutUI/Icons/Close";
import BannerPicker from "../rooms/BannerPicker";
import moment from "moment";
import { uploadMedia } from "../../utils/uploadMedia";
import momenttimezone from "moment-timezone";
import CurrencyInput from "react-currency-input-field";
import VisEye from "../../VibehutUI/Icons/VisEye";
import HidEye from "../../VibehutUI/Icons/HidEye";
import LimitedTextarea from "../../VibehutUI/LimitedTextarea";
import ReactGoogleAutocomplete from "react-google-autocomplete";

export default function EditEvent({
  isOpen,
  setIsOpen,
  event,
  isFromEventPage = false,
  getEvent = undefined,
}) {
  const { state, dispatch } = useContext(StoreContext);

  const {
    auth: {
      user: { token },
    },
    event: { isFetching },
  } = state;

  const formatForDateInput = (date) => {
    return moment(date).format("YYYY-MM-DDTHH:mm");
  };

  const [selectedLocation, setSelectedLocation] = useState({
    formatted_address: event.location?.formatted_address || "",
    lat: event.location?.geometry?.location?.lat || "",
    lng: event.location?.geometry?.location?.lng || "",
  });

  const [editEvent, setEvent] = useState({
    title: event.title,
    description: event.description,
    startDate: formatForDateInput(new Date(event.startDate)),
    endDate: formatForDateInput(new Date(event.endDate)),
    link: event.extLink || "",
    isPaidEvent: event.isPaidEvent,
    isExternalEvent: event.isExternalEvent,
    price: String(event?.price || 0),
  });
  const cancelButtonRef = useRef(null);
  const [cover, setCover] = useState(event.cover);
  const [coverOpen, setCoverOpen] = useState(false);
  const [visError, setVisError] = useState(false);

  const [coverLoading, setCoverLoading] = useState(false);
  const [coverFile, setCoverFile] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [showPaymentErr, setShowPaymentErr] = useState("");
  const [isPasswordProtected, setIsPasswordProtected] = useState(
    event?.isPasswordProtected,
  );
  const [isRequireApproval, setIsRequireApproval] = useState(
    event?.isRequireApproval,
  );
  const [isGroupEdit, setIsGroupEdit] = useState(false);
  const [isPassVisible, setIsPassVisible] = useState(false);
  const [showPasswordErr, setShowPasswordErr] = useState(false);
  const [password, setPassword] = useState(event?.password);
  const [isMaxRsvp, setIsMaxRsvp] = useState(event?.maxRsvp ? true : false);
  const [maxRsvp, setMaxRsvp] = useState(
    event?.maxRsvp || event?.room?.maxUsersPerGroup,
  );
  const [showMaxRsvpErr, setShowMaxRsvpErr] = useState("");

  const handleLocationChange = (e) => {
    setSelectedLocation({ formatted_address: e.target.value });
  };

  const handleChange = (e) => {
    setEvent({ ...editEvent, [e.target.name]: e.target.value });
  };

  const handleDescriptionChange = (text) => {
    setEvent((prev) => ({ ...prev, description: text }));
  };

  const handleCurrencyInputChange = (e) => {
    e.preventDefault();
    const { value = "" } = e.target;
    const parsedValue = value.replace(/[^\d.]/gi, "");
    if (isNaN(parsedValue)) return;
    setEvent((prev) => {
      return {
        ...prev,
        price: parsedValue,
      };
    });
    setShowPaymentErr("");
  };

  const handleDateChange = (e) => {
    if (e.target.name === "startDate") {
      const endDate = new Date(e.target.value);
      endDate.setHours(endDate.getHours() + 1);
      setEvent({
        ...editEvent,
        startDate: formatForDateInput(new Date(e.target.value)),
        endDate: formatForDateInput(endDate),
      });
    } else {
      setVisError(false);
      setEvent({
        ...editEvent,
        [e.target.name]: formatForDateInput(new Date(e.target.value)),
      });
    }
  };

  const handleUpdateEvent = (media = undefined) => {
    const startDate = new Date(editEvent.startDate);
    const endDate = new Date(editEvent.endDate);
    if (isPasswordProtected && password === "") {
      setShowPasswordErr(true);
    }
    if (
      (Number.isNaN(parseInt(editEvent.price)) ||
        Number(editEvent.price) === 0) &&
      editEvent.isPaidEvent
    ) {
      if (Number(editEvent.price) === 0) {
        setShowPaymentErr(" Minimum amount to charge is $1.00");
        setTimeout(() => {
          setShowPaymentErr("");
        }, 5000);
        return;
      } else {
        setShowPaymentErr("Please enter a valid amount");
        setTimeout(() => {
          setShowPaymentErr("");
        }, 5000);
        return;
      }
    }
    if (
      (endDate.getDate() < startDate.getDate() &&
        endDate.getMonth <= startDate.getMonth()) ||
      (endDate.getDate() === startDate.getDate() &&
        endDate.getHours() < startDate.getHours()) ||
      (endDate.getDate() === startDate.getDate() &&
        endDate.getHours() === startDate.getHours() &&
        endDate.getMinutes() < startDate.getMinutes())
    ) {
      setVisError(true);
      return;
    }
    if (isMaxRsvp && !maxRsvp) {
      setShowMaxRsvpErr("Enter any valid number");
      return;
    }
    if (isMaxRsvp && Number(maxRsvp) <= 0) {
      setShowMaxRsvpErr("0 and negative values are not allowed");
      return;
    }
    const title = editEvent.title;
    const description = editEvent.description;
    const isPaidEvent = editEvent.isPaidEvent;
    const isExternalEvent = editEvent.isExternalEvent;
    const price = editEvent.price;

    updateEvent(
      {
        title,
        description,
        startDate,
        endDate,
        isPaidEvent,
        price,
        creatorTimeZone: momenttimezone.tz.guess(),
        cover: media?.url
          ? {
              urls: {
                raw: media.url,
                full: media.url,
                regular: media.url,
                small: media.url,
                thumb: media.url,
              },
              source: "custom",
            }
          : cover,
        isPasswordProtected,
        isGroupEdit,
        isExternalEvent,
        ...(isExternalEvent
          ? {
              location: selectedLocation || {},
              extLink: editEvent.link,
            }
          : {
              location: {},
              extLink: "",
            }),
        ...(password && { password: password }),
        ...(isMaxRsvp && maxRsvp && { maxRsvp: Number(maxRsvp) }),
        isRequireApproval,
      },
      event._id,
      token,
      dispatch,
    )
      .then((res) => {
        updateEventCacheData(event.room?._id, event._id);

        setIsOpen(false);
        if (getEvent && isGroupEdit) {
          getEvent();
        }
        if (isFromEventPage) {
          getEvent();
        }
      })
      .catch((err) => {
        console.log("err", err);
      });
  };
  const handleUploadAndUpdateEvent = async () => {
    if (coverFile) {
      await uploadMedia(
        coverFile,
        cover?.urls?.small,
        setIsImageUploading,
        setCover,
        setCoverFile,
        handleUpdateEvent,
      );
    } else {
      await handleUpdateEvent();
    }
  };

  const handlePasswordChange = (e) => {
    if (e.target.value.length <= 16) {
      setPassword(e.target.value);
      setShowPasswordErr(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-40 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={setIsOpen}
      >
        <div className="flex md:items-end items-start justify-center md:min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-overlay bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white dark:bg-navyGray rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-450px font-ubuntu">
              <div className="bg-white dark:bg-navyGray p-4 border-b border-frescoWhite dark:border-matteGray">
                <div className="flex justify-between items-center">
                  <div className="flex flex-row justify-start items-center">
                    {coverOpen ? (
                      <span
                        className="bg-frescoWhite rounded-full p-1 cursor-pointer text-gray-600 mr-2"
                        onClick={() => {
                          setCoverOpen(false);
                        }}
                      >
                        <Back />
                      </span>
                    ) : undefined}
                    <p className="font-medium text-lg text-black dark:text-frescoWhite">
                      {coverOpen ? "Change Cover" : "Edit Event"}
                    </p>
                  </div>
                  <span
                    className="bg-frescoWhite p-9px rounded-full cursor-pointer"
                    onClick={() => {
                      setCoverOpen(false);
                      setIsOpen(false);
                    }}
                  >
                    <Close />
                  </span>
                </div>
              </div>
              {coverOpen ? (
                <BannerPicker
                  setCover={setCover}
                  setCoverLoading={setCoverLoading}
                  setCoverOpen={setCoverOpen}
                  setCoverFile={setCoverFile}
                />
              ) : (
                <div className="md:max-h-[80vh] max-h-[70vh] overflow-auto custom-scrollbar transform transition-all">
                  <div className="bg-white dark:bg-navyGray p-4 relative flex space-x-3 ">
                    <div className="w-56">
                      {(cover?.urls?.small || !!coverFile) && (
                        <img
                          alt="room"
                          src={
                            coverFile
                              ? URL.createObjectURL(coverFile)
                              : cover?.urls?.small
                          }
                          className="rounded-xl object-cover w-full"
                          loading="lazy"
                        />
                      )}
                    </div>
                    <div className="">
                      <p className="text-base font-medium text-darkNight dark:text-frescoWhite">
                        Event Cover
                      </p>
                      <p className="text-sm font-normal text-darkGray">
                        This cover will appear as the event thumbnail.
                      </p>
                      {coverLoading ? (
                        <div className="centerAbsolute mt-1 text-sm py-2 px-3 font-normal rounded-2xl">
                          <Loader />
                        </div>
                      ) : (
                        <div className="pt-2">
                          <Button
                            onClick={() => setCoverOpen(true)}
                            size="vibhut-sm"
                            variant="vibhut-primary"
                          >
                            Change
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="px-4">
                    <div>
                      <p className="text-sm font-medium text-darkNight dark:text-frescoWhite">
                        Event Title*
                      </p>
                      <input
                        type="text"
                        className="md:text-sm text-base border outline-none border-primaryGray py-2 px-4 rounded-xl my-1.5 w-full placeholder-notifyBg "
                        value={editEvent.title}
                        onChange={handleChange}
                        name="title"
                        placeholder="E.g. Producivity Hacks"
                      />
                    </div>
                    <div className="mt-3">
                      <p className="text-sm font-medium text-darkNight dark:text-frescoWhite">
                        Event Description
                      </p>
                      <LimitedTextarea
                        limit={2000}
                        value={editEvent.description || ""}
                        rows={5}
                        placeHolder="E.g. Describe your event"
                        name="description"
                        onChange={handleDescriptionChange}
                      />
                    </div>
                    <div
                      className={`flex justify-between items-center py-1.5 pb-2`}
                    >
                      <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                        Require Approval to Join Event ?
                      </p>

                      <Switch
                        checked={isRequireApproval}
                        onChange={setIsRequireApproval}
                        className={`${
                          isRequireApproval ? "bg-primary" : "bg-gray-200"
                        } relative inline-flex items-center  rounded-full h-31px w-51px float-right`}
                      >
                        <span
                          className={`${
                            isRequireApproval
                              ? "translate-x-6"
                              : "translate-x-1"
                          } inline-block w-27px h-27px transform bg-white shadow rounded-full duration-200`}
                        />
                      </Switch>
                    </div>
                    {event.isRecurrEvent && (
                      <div
                        className={`flex justify-between items-center ${
                          isGroupEdit ? " pt-1.5" : " py-1.5"
                        }`}
                      >
                        <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                          Is Edit all recurring events ?
                        </p>

                        <Switch
                          checked={isGroupEdit}
                          name="isGroupEdit"
                          onChange={() => {
                            setIsGroupEdit(!isGroupEdit);
                          }}
                          className={`${
                            isGroupEdit ? "bg-primary" : "bg-gray-200"
                          } relative inline-flex items-center  rounded-full h-31px w-51px float-right`}
                        >
                          <span
                            className={`${
                              isGroupEdit ? "translate-x-6" : "translate-x-1"
                            } inline-block w-27px h-27px transform bg-white shadow rounded-full duration-200`}
                          />
                        </Switch>
                      </div>
                    )}
                    <div
                      className={`flex justify-between items-center ${
                        editEvent.isExternalEvent ? "pt-1.5" : " py-1.5 pb-2"
                      }`}
                    >
                      <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                        Is This External Event ?
                      </p>

                      <Switch
                        checked={editEvent.isExternalEvent}
                        onChange={() => {
                          setEvent({
                            ...editEvent,
                            isExternalEvent: !editEvent.isExternalEvent,
                          });
                        }}
                        className={`${
                          editEvent.isExternalEvent
                            ? "bg-primary"
                            : "bg-gray-200"
                        } relative inline-flex items-center  rounded-full h-31px w-51px float-right`}
                      >
                        <span
                          className={`${
                            editEvent.isExternalEvent
                              ? "translate-x-6"
                              : "translate-x-1"
                          } inline-block w-27px h-27px transform bg-white shadow rounded-full duration-200`}
                        />
                      </Switch>
                    </div>
                    {editEvent.isExternalEvent && (
                      <>
                        <div className="pb-2">
                          <p className="text-sm font-medium text-darkNight dark:text-frescoWhite">
                            Location
                          </p>
                          <ReactGoogleAutocomplete
                            className="border outline-none border-primaryGray py-2 px-4 rounded-xl my-1 w-full placeholder-notifyBg text-sm"
                            placeholder="Enter Location"
                            debounce="1000"
                            value={selectedLocation.formatted_address}
                            onChange={handleLocationChange}
                            onPlaceSelected={(place) => {
                              setSelectedLocation({
                                formatted_address:
                                  place.formatted_address || "",
                                lat: `${place.geometry?.location?.lat() || ""}`,
                                lng: `${place.geometry?.location?.lng() || ""}`,
                              });
                            }}
                          />
                        </div>
                        <div className="pt-2">
                          <p className="text-sm font-medium text-darkNight dark:text-frescoWhite">
                            External URL (Zoom, EventBrite)
                          </p>
                          <input
                            type="text"
                            className="border  outline-none border-primaryGray py-2 px-4 rounded-xl my-1 w-full placeholder-notifyBg text-sm"
                            value={editEvent.link}
                            name="link"
                            onChange={handleChange}
                            placeholder="Any other url"
                          />
                        </div>
                      </>
                    )}

                    <div
                      className={`flex justify-between items-center ${
                        editEvent.isPaidEvent ? " pt-1.5" : " py-1.5"
                      }`}
                    >
                      <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                        Is Paid Event ?
                      </p>

                      <Switch
                        checked={editEvent.isPaidEvent}
                        name="isPaidEvent"
                        onChange={() => {
                          setEvent({
                            ...editEvent,
                            isPaidEvent: !editEvent.isPaidEvent,
                          });
                        }}
                        className={`${
                          editEvent.isPaidEvent ? "bg-primary" : "bg-gray-200"
                        } relative inline-flex items-center  rounded-full h-31px w-51px float-right`}
                      >
                        <span
                          className={`${
                            editEvent.isPaidEvent
                              ? "translate-x-6"
                              : "translate-x-1"
                          } inline-block w-27px h-27px transform bg-white shadow rounded-full duration-200`}
                        />
                      </Switch>
                    </div>
                    {editEvent.isPaidEvent && (
                      <div className="flex flex-row justify-between w-full items-center py-1.5">
                        <div>
                          <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                            Amount
                          </p>
                        </div>
                        <div>
                          <CurrencyInput
                            prefix="$"
                            name="currencyInput"
                            id="currencyInput"
                            data-number-to-fixed="2"
                            data-number-stepfactor="100"
                            value={editEvent.price}
                            placeholder="$0.00"
                            className="text-sm border outline-none border-primaryGray py-2 px-2 rounded-xl my-1 w-full"
                            onChange={(e) => {
                              handleCurrencyInputChange(e);
                            }}
                            onBlur={() =>
                              setEvent((prev) => {
                                return {
                                  ...prev,
                                  price: Number(editEvent.price).toFixed(2),
                                };
                              })
                            }
                            allowDecimals
                            decimalsLimit="2"
                            decimalScale="2"
                            disableAbbreviations
                          />
                          {showPaymentErr && (
                            <p className={` text-xs text-cvRed`}>
                              {showPaymentErr}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    <div
                      className={`flex justify-between items-center ${
                        isPasswordProtected ? " pt-1.5" : " py-1.5"
                      }`}
                    >
                      <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                        Is This Password Protected Event ?
                      </p>

                      <Switch
                        checked={isPasswordProtected}
                        onChange={() =>
                          setIsPasswordProtected(!isPasswordProtected)
                        }
                        className={`${
                          isPasswordProtected ? "bg-primary" : "bg-gray-200"
                        } relative inline-flex items-center  rounded-full h-31px w-51px float-right`}
                      >
                        <span
                          className={`${
                            isPasswordProtected
                              ? "translate-x-6"
                              : "translate-x-1"
                          } inline-block w-27px h-27px transform bg-white shadow rounded-full duration-200`}
                        />
                      </Switch>
                    </div>
                    <div className={`${isPasswordProtected ? "" : "hidden"} `}>
                      <div className="relative w-full">
                        <input
                          type={isPassVisible ? "text" : "password"}
                          className={`text-sm border ${
                            showPasswordErr
                              ? "border-red-500"
                              : "border-primaryGray"
                          } outline-none  py-2 px-4 rounded-xl my-1.5 w-full placeholder-notifyBg relative`}
                          value={password}
                          max={16}
                          onChange={handlePasswordChange}
                          placeholder="Password"
                        />
                        <span
                          className="cursor-pointer absolute top-4 right-3 z-10"
                          onClick={() => setIsPassVisible(!isPassVisible)}
                        >
                          {isPassVisible ? <VisEye /> : <HidEye />}
                        </span>
                      </div>
                      {showPasswordErr ? (
                        <p className={` text-xs text-cvRed`}>
                          Password can't be empty
                        </p>
                      ) : null}

                      <p className={` text-gray-400 text-xs`}>
                        16 Characters max
                      </p>
                    </div>
                    <>
                      <div
                        className={`flex justify-between items-center ${
                          isMaxRsvp ? " pt-1.5" : " py-1.5"
                        }`}
                      >
                        <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                          Max RSVP
                        </p>

                        <Switch
                          checked={isMaxRsvp}
                          onChange={() => setIsMaxRsvp(!isMaxRsvp)}
                          className={`${
                            isMaxRsvp ? "bg-primary" : "bg-gray-200"
                          } relative inline-flex items-center  rounded-full h-31px w-51px float-right`}
                        >
                          <span
                            className={`${
                              isMaxRsvp ? "translate-x-6" : "translate-x-1"
                            } inline-block w-27px h-27px transform bg-white shadow rounded-full duration-200`}
                          />
                        </Switch>
                      </div>
                      {isMaxRsvp && (
                        <div className="flex flex-row justify-between w-full items-center py-1.5">
                          <div>
                            <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                              Total
                            </p>
                          </div>
                          <div>
                            <input
                              type="number"
                              className={` border ${
                                !showMaxRsvpErr
                                  ? "border-primaryGray"
                                  : "border-red-500"
                              } text-sm placeholder-gray-400  border  outline-none  py-2 px-2 rounded-xl my-1  w-54 `}
                              value={maxRsvp}
                              onChange={(e) => setMaxRsvp(e.target.value)}
                              name="endDate"
                              min={1}
                            />
                            {!!showMaxRsvpErr && (
                              <p className={` text-xs text-cvRed`}>
                                {showMaxRsvpErr}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                    <div className="flex pt-1.5 justify-between items-center">
                      <div className="w-1/2">
                        <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                          Start Time*
                        </p>
                        <p className="font-normal text-sm text-darkGray">
                          When will the event begin?
                        </p>
                      </div>
                      <div className="">
                        <input
                          type="datetime-local"
                          className="text-sm border outline-none border-primaryGray py-2 px-2 rounded-xl my-1 w-54"
                          value={editEvent.startDate}
                          onChange={handleDateChange}
                          name="startDate"
                          min={new Date().toISOString().slice(0, 16)}
                        />
                      </div>
                    </div>
                    <div className="w-full  border-b border-frescoWhite dark:border-matteGray py-1.5"></div>
                    <div className="flex pt-1.5 justify-between items-center">
                      <div className="">
                        <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                          End Time*
                        </p>
                        <p className="font-normal text-sm text-darkGray">
                          When will it end?
                        </p>
                      </div>
                      <div className="">
                        <input
                          type="datetime-local"
                          className={` border ${
                            !visError ? "border-primaryGray" : "border-red-500"
                          } text-sm placeholder-gray-400  border  outline-none  py-2 px-2 rounded-xl my-1  w-54 `}
                          value={editEvent.endDate}
                          onChange={handleDateChange}
                          name="endDate"
                          min={formatForDateInput(editEvent.startDate)}
                        />
                      </div>
                    </div>
                    {visError && (
                      <p className={`pt-1 text-xs text-cvRed`}>
                        * End time should be greater then Start time
                      </p>
                    )}
                    {/* <div className="w-full  border-b border-frescoWhite dark:border-matteGray py-1.5"></div>
                    <div className="flex justify-between pt-1.5 items-center">
                      <div className="">
                        <p className="text-sm font-medium text-navyGray dark:text-frescoWhite">
                          Time Zone
                        </p>
                        <p className="font-normal text-sm text-darkGray">
                          What time zone is this event in?
                        </p>
                      </div>
                      <div>
                        <select
                          name=""
                          id=""
                          className="text-sm border outline-none border-primaryGray py-2 px-4 rounded-xl my-1 w-46"
                        >
                          <option value="est">Est</option>
                        </select>
                      </div>
                    </div> */}
                  </div>
                  <div className=" px-4 py-3 flex flex-row-reverse ">
                    {isFetching || isImageUploading ? (
                      <Loader />
                    ) : (
                      <Button
                        onClick={handleUploadAndUpdateEvent}
                        size="vibhut-sm"
                        variant="vibhut-primary"
                      >
                        Update
                      </Button>
                    )}
                    <div className="mr-3">
                      <Button
                        size="vibhut-sm"
                        variant="vibhut-secondary"
                        onClick={() => setIsOpen(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
